import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Gavel, Clock, DollarSign, Users, AlertTriangle, 
  CheckCircle, XCircle, TrendingUp, FileText, Building2,
  Timer, Trophy, ChevronRight, Filter, Search
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function QuickBidPage() {
  const [activeTab, setActiveTab] = useState('active');
  const [quickbids, setQuickbids] = useState([]);
  const [complianceRecords, setComplianceRecords] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedBid, setSelectedBid] = useState(null);
  const [hospitalId, setHospitalId] = useState('');
  const [thresholdEdit, setThresholdEdit] = useState({ 
    enabled: false,
    amount: 2500, 
    window: 60, 
    minBids: 3,
    soleSourceExempt: true,
    adminOverride: true,
    hospitalCanDisable: true
  });
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');

  const fetchQuickBids = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/quickbid/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setQuickbids(data.quickbids || []);
    } catch (err) {
      console.error('Error fetching quickbids:', err);
    }
  }, [token]);

  const fetchComplianceRecords = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/quickbid/compliance/report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setComplianceRecords(data.records || []);
    } catch (err) {
      console.error('Error fetching compliance records:', err);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      if (!token) {
        setError('Please log in to access QuickBid');
        setLoading(false);
        return;
      }
      setLoading(true);
      await Promise.all([fetchQuickBids(), fetchComplianceRecords()]);
      setLoading(false);
    };
    loadData();
  }, [fetchQuickBids, fetchComplianceRecords, token]);

  const fetchSettings = async (hsId) => {
    if (!hsId) return;
    try {
      const res = await fetch(`${API}/api/quickbid/settings/${hsId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSettings(data);
      setThresholdEdit({
        enabled: data.enabled || false,
        amount: data.threshold_amount || 2500,
        window: data.bid_window_minutes || 60,
        minBids: data.require_minimum_bids || 3,
        soleSourceExempt: data.sole_source_exempt !== false,
        adminOverride: data.admin_override_allowed !== false,
        hospitalCanDisable: data.hospital_can_disable !== false
      });
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const toggleQuickBid = async (enable) => {
    if (!hospitalId) return;
    try {
      const endpoint = enable ? 'enable' : 'disable';
      await fetch(`${API}/api/quickbid/${endpoint}/${hospitalId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchSettings(hospitalId);
    } catch (err) {
      console.error('Error toggling QuickBid:', err);
    }
  };

  const saveSettings = async () => {
    if (!hospitalId) return;
    try {
      await fetch(`${API}/api/quickbid/settings/${hospitalId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: thresholdEdit.enabled,
          threshold_amount: thresholdEdit.amount,
          bid_window_minutes: thresholdEdit.window,
          require_minimum_bids: thresholdEdit.minBids,
          auto_notify_suppliers: true,
          sole_source_exempt: thresholdEdit.soleSourceExempt,
          admin_override_allowed: thresholdEdit.adminOverride,
          hospital_can_disable: thresholdEdit.hospitalCanDisable,
          categories: []
        })
      });
      alert('Settings saved successfully!');
      fetchSettings(hospitalId);
    } catch (err) {
      console.error('Error saving settings:', err);
    }
  };

  const awardBid = async (quickbidId, bidId) => {
    try {
      const res = await fetch(`${API}/api/quickbid/${quickbidId}/award`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ bid_id: bidId })
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert(`Bid awarded! Savings: $${data.savings?.toLocaleString() || 0}`);
        fetchQuickBids();
        fetchComplianceRecords();
        setSelectedBid(null);
      }
    } catch (err) {
      console.error('Error awarding bid:', err);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      awarded: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.toUpperCase()}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    const styles = {
      emergency: 'bg-red-500 text-white',
      urgent: 'bg-orange-500 text-white',
      standard: 'bg-blue-100 text-blue-800'
    };
    return <Badge className={styles[urgency] || 'bg-gray-100'}>{urgency}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Gavel className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">QuickBid Enterprise</h2>
            <p className="text-gray-600 mb-4">
              {error || 'Please log in as an admin to access the QuickBid Center'}
            </p>
            <Button onClick={() => window.location.href = '/staff-login'}>
              Staff Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="quickbid-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-blue-600 rounded-xl">
            <Gavel className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">QuickBid Center</h1>
            <p className="text-gray-600">Enterprise Competitive Bidding & Compliance</p>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Automated audit trails for high-threshold hospital procurement (&gt;$2,500)
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Bids</p>
                <p className="text-2xl font-bold text-green-600">{quickbids.length}</p>
              </div>
              <Timer className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Compliance Records</p>
                <p className="text-2xl font-bold text-blue-600">{complianceRecords.length}</p>
              </div>
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Savings</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ${complianceRecords.reduce((sum, r) => sum + (r.savings_vs_estimate || 0), 0).toLocaleString()}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Bids/Request</p>
                <p className="text-2xl font-bold text-purple-600">
                  {complianceRecords.length > 0 
                    ? (complianceRecords.reduce((sum, r) => sum + (r.total_bids_received || 0), 0) / complianceRecords.length).toFixed(1)
                    : '0'}
                </p>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="active">Active QuickBids</TabsTrigger>
          <TabsTrigger value="compliance">Compliance Records</TabsTrigger>
          <TabsTrigger value="settings">Threshold Settings</TabsTrigger>
        </TabsList>

        {/* Active QuickBids Tab */}
        <TabsContent value="active">
          {quickbids.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Gavel className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-600">No Active QuickBids</h3>
                <p className="text-gray-500">QuickBids are automatically created when request values exceed thresholds</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {quickbids.map((qb) => (
                <Card key={qb.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">{qb.title}</h3>
                          {getStatusBadge(qb.status)}
                          {getUrgencyBadge(qb.urgency)}
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{qb.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {qb.hospital_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            Est: ${qb.estimated_value?.toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {qb.bid_count || 0} bids
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {qb.minutes_remaining || 0} min left
                          </span>
                        </div>
                      </div>
                      <Button 
                        onClick={() => setSelectedBid(qb)}
                        className="flex items-center gap-2"
                      >
                        View Bids <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Compliance Records Tab */}
        <TabsContent value="compliance">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Compliance Audit Trail
              </CardTitle>
              <CardDescription>
                Complete procurement records for Texas hospital compliance requirements
              </CardDescription>
            </CardHeader>
            <CardContent>
              {complianceRecords.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No compliance records yet. Records are created when QuickBids are awarded.
                </div>
              ) : (
                <div className="space-y-4">
                  {complianceRecords.map((record) => (
                    <div key={record.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircle className="w-5 h-5 text-green-500" />
                            <span className="font-medium">{record.id}</span>
                            <Badge variant="outline">{record.request_type}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{record.hospital_name}</p>
                          <div className="flex gap-4 text-sm">
                            <span>Bids: {record.total_bids_received}</span>
                            <span className="text-green-600">
                              Winner: {record.winning_bid?.supplier_name}
                            </span>
                            <span className="text-blue-600">
                              Savings: ${record.savings_vs_estimate?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <p>${record.winning_bid?.amount?.toLocaleString()}</p>
                          <p>{new Date(record.awarded_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>QuickBid Settings & Controls</CardTitle>
              <CardDescription>
                Configure competitive bidding per health system. QuickBid is OPT-IN — hospitals and admins have full control.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Health System ID</label>
                  <div className="flex gap-2">
                    <Input 
                      value={hospitalId}
                      onChange={(e) => setHospitalId(e.target.value)}
                      placeholder="Enter health system ID"
                    />
                    <Button onClick={() => fetchSettings(hospitalId)}>Load Settings</Button>
                  </div>
                </div>

                {settings.health_system_id && (
                  <div className="border rounded-lg p-6 space-y-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-lg">Settings for: {settings.health_system_id}</h4>
                        <p className="text-sm text-gray-500">QuickBid competitive bidding controls</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {settings.enabled ? (
                          <Badge className="bg-green-100 text-green-800">ENABLED</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">DISABLED</Badge>
                        )}
                        <Button 
                          variant={settings.enabled ? "destructive" : "default"}
                          onClick={() => toggleQuickBid(!settings.enabled)}
                        >
                          {settings.enabled ? 'Disable QuickBid' : 'Enable QuickBid'}
                        </Button>
                      </div>
                    </div>

                    {/* Master Toggle Info */}
                    <div className={`p-4 rounded-lg ${settings.enabled ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <p className="text-sm">
                        {settings.enabled 
                          ? '✓ QuickBid is ACTIVE for this health system. High-value purchases will prompt competitive bidding.'
                          : '○ QuickBid is INACTIVE. All purchases will use direct dispatch without competitive bidding.'}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Threshold Amount ($)
                        </label>
                        <Input 
                          type="number"
                          value={thresholdEdit.amount}
                          onChange={(e) => setThresholdEdit({...thresholdEdit, amount: parseFloat(e.target.value)})}
                          disabled={!settings.enabled}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Requests at or above this value trigger QuickBid
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Bid Window (minutes)
                        </label>
                        <Input 
                          type="number"
                          value={thresholdEdit.window}
                          onChange={(e) => setThresholdEdit({...thresholdEdit, window: parseInt(e.target.value)})}
                          disabled={!settings.enabled}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Time suppliers have to submit bids
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Minimum Bids Required
                        </label>
                        <Input 
                          type="number"
                          value={thresholdEdit.minBids}
                          onChange={(e) => setThresholdEdit({...thresholdEdit, minBids: parseInt(e.target.value)})}
                          disabled={!settings.enabled}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Recommended: 3 for compliance
                        </p>
                      </div>
                    </div>

                    {/* Override Options */}
                    <div className="border-t pt-4">
                      <h5 className="font-medium mb-3">Override & Exemption Options</h5>
                      <div className="space-y-3">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={thresholdEdit.soleSourceExempt}
                            onChange={(e) => setThresholdEdit({...thresholdEdit, soleSourceExempt: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <div>
                            <span className="font-medium">Allow Sole Source Exemptions</span>
                            <p className="text-xs text-gray-500">Skip QuickBid for sole source purchases with justification</p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={thresholdEdit.adminOverride}
                            onChange={(e) => setThresholdEdit({...thresholdEdit, adminOverride: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <div>
                            <span className="font-medium">Admin Override Allowed</span>
                            <p className="text-xs text-gray-500">Admins can bypass threshold for specific requests</p>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input 
                            type="checkbox" 
                            checked={thresholdEdit.hospitalCanDisable}
                            onChange={(e) => setThresholdEdit({...thresholdEdit, hospitalCanDisable: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <div>
                            <span className="font-medium">Hospital Can Disable Per-Request</span>
                            <p className="text-xs text-gray-500">Hospital staff can opt-out of QuickBid for individual requests</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    <Button onClick={saveSettings} className="w-full">
                      Save All Settings
                    </Button>
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-900">Texas Hospital Compliance (Optional)</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        For purchases exceeding $2,500-$5,000, Texas hospital systems may require 
                        documented competitive bidding. QuickBid automates this process when enabled,
                        but can be turned off for sole source or exempt purchases.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Bid Review Modal */}
      {selectedBid && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle>{selectedBid.title}</CardTitle>
                  <CardDescription>
                    Review and award bids - {selectedBid.bid_count || 0} bids received
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedBid(null)}>
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Estimated Value:</span>
                      <span className="ml-2 font-medium">${selectedBid.estimated_value?.toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Hospital:</span>
                      <span className="ml-2 font-medium">{selectedBid.hospital_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Category:</span>
                      <span className="ml-2 font-medium">{selectedBid.category}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Time Remaining:</span>
                      <span className="ml-2 font-medium">{selectedBid.minutes_remaining} minutes</span>
                    </div>
                  </div>
                </div>

                <h4 className="font-medium flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  Top Bids (Ranked by Price)
                </h4>

                {selectedBid.bids?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedBid.bids.slice(0, 5).map((bid, index) => (
                      <div 
                        key={bid.bid_id} 
                        className={`border rounded-lg p-4 ${index === 0 ? 'border-yellow-400 bg-yellow-50' : ''}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              {index === 0 && <Trophy className="w-4 h-4 text-yellow-500" />}
                              <span className="font-medium">{bid.supplier_name}</span>
                              {bid.supplier_rating > 0 && (
                                <Badge variant="outline">★ {bid.supplier_rating}</Badge>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              Lead time: {bid.lead_time_days} days
                              {bid.includes_freight && ' • Freight included'}
                              {bid.warranty_months && ` • ${bid.warranty_months}mo warranty`}
                            </div>
                            {bid.notes && (
                              <p className="text-sm text-gray-600 mt-2">{bid.notes}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-green-600">
                              ${bid.amount?.toLocaleString()}
                            </p>
                            <Button 
                              size="sm" 
                              className="mt-2"
                              onClick={() => awardBid(selectedBid.id, bid.bid_id)}
                            >
                              Award Bid
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No bids received yet. Waiting for suppliers to respond.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
